// frontend/src/components/ChatSidebar/DeceasedSelectionMessage.tsx
/**
 * Displays matching livestock for user selection in deceased chat flow.
 * Uses red color theme to indicate the solemn nature of the operation.
 */
import { Paper, Stack, Text, Button, Group } from '@mantine/core';
import { IconSkull } from '@tabler/icons-react';
import type { LivestockMatch, ConfirmLivestockDeceasedRequest } from '@/api/chat';

interface DeceasedSelectionMessageProps {
  matches: LivestockMatch[];
  onSelect: (data: ConfirmLivestockDeceasedRequest) => void;
  loading: boolean;
}

export function DeceasedSelectionMessage({
  matches,
  onSelect,
  loading,
}: DeceasedSelectionMessageProps) {
  const today = new Date().toISOString().split('T')[0];

  const handleSelect = (match: LivestockMatch) => {
    onSelect({
      livestock_id: match.id,
      deceased_date: today,
    });
  };

  if (matches.length === 0) {
    return (
      <Paper p="sm" radius="md" bg="orange.0">
        <Text size="sm">No matching livestock found in your tank.</Text>
      </Paper>
    );
  }

  return (
    <Paper p="sm" radius="md" withBorder>
      <Stack gap="xs">
        <Group gap="xs">
          <IconSkull size={16} color="var(--mantine-color-red-6)" />
          <Text size="sm" fw={500}>
            Which one passed away?
          </Text>
        </Group>

        {matches.map((match) => (
          <Button
            key={match.id}
            variant="light"
            color="red"
            size="xs"
            fullWidth
            disabled={loading}
            onClick={() => handleSelect(match)}
          >
            <Group justify="space-between" w="100%">
              <Text size="xs">{match.common_name}</Text>
              <Text size="xs" c="dimmed">
                Added {new Date(match.acquisition_date).toLocaleDateString()}
              </Text>
            </Group>
          </Button>
        ))}
      </Stack>
    </Paper>
  );
}
